import React, { useRef, useState } from 'react';
import { Pressable, TextInput, type NativeSyntheticEvent, type TextInputSelectionChangeEventData } from 'react-native';
import { Text, XStack, YStack } from 'tamagui';
import type { IconName } from '@kiips/ui';
import { SectionLabel, useFocusAndInsert, useThemeColors, Icon } from '@kiips/ui';
import { NoteMarkdown } from '../design/NoteMarkdown';

/**
 * Reusable inline notes editor used by every itinerary detail page via
 * EntityEditView. Owns its own draft state + selection + edit toggle.
 * The parent supplies the current note content and a setter (typically
 * wired to useItineraryStore.setNote(tripId, entityId, content)).
 *
 * In view mode a long note renders a short preview (first few lines)
 * with a "Show more" toggle; tapping the body expands it, and tapping
 * an expanded note opens the editor.
 *
 * Toolbar buttons insert checklist (- [ ]), hashtag (#), bold (**...**),
 * and heading (#) at the cursor — same surface as /place/[id].
 */

/** Notes longer than this collapse to a preview in view mode. */
const PREVIEW_LINES = 3;
const PREVIEW_CHARS = 240;

/** Trim a note down to its preview form. `truncated` is true when the
 *  preview dropped content — i.e. the caller should offer "Show more". */
function buildPreview(content: string): { preview: string; truncated: boolean } {
  const lines = content.split('\n');
  let preview = content;
  let truncated = false;
  if (lines.length > PREVIEW_LINES) {
    preview = lines.slice(0, PREVIEW_LINES).join('\n');
    truncated = true;
  }
  if (preview.length > PREVIEW_CHARS) {
    preview = preview.slice(0, PREVIEW_CHARS).trimEnd();
    truncated = true;
  }
  return { preview, truncated };
}
interface NotesEditorBlockProps {
  /** Current saved note content (markdown). */
  content: string | undefined;
  /** Persist a new note. Pass undefined or '' to clear. */
  onSave: (next: string) => void;
  /** When false, render in read-only mode — no toolbar, no edit affordance. */
  canEdit: boolean;
  /** Placeholder shown inside the textarea while editing an empty note. */
  placeholder?: string | undefined;
}

export function NotesEditorBlock({
  content,
  onSave,
  canEdit,
  placeholder = 'Add a note…',
}: NotesEditorBlockProps) {
  const theme = useThemeColors();
  const [editing, setEditing] = useState(false);
  // A long note opens collapsed; the user expands it before it can be
  // edited (tap to expand, tap again to edit).
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  // Focus + insert + selection live in the shared hook (ADR-0079 audit
  // — extracted from the prior inline implementation here).
  const {
    inputRef,
    selection,
    setSelection,
    focusAtCursor,
    insertAtSelection: insertAt,
  } = useFocusAndInsert();
  const [overrides, setOverrides] = useState<Record<number, boolean>>({});
  // Tapping a toolbar button momentarily steals focus from the textarea on
  // web. Without this flag, onBlur would fire and call save(), closing
  // the editor before the toolbar's insert can run.
  const toolbarInteractingRef = useRef(false);

  const insertAtSelection = (text: string) => {
    const { next, cursor } = insertAt(draft, text);
    setDraft(next);
    focusAtCursor(cursor);
  };
  const insertChecklist = () => {
    const { start } = selection;
    const lineStart = draft.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const onEmptyLine = lineStart === start;
    insertAtSelection(onEmptyLine ? '- [ ] ' : '\n- [ ] ');
  };
  const insertTag = () => insertAtSelection('#');
  const insertBold = () => {
    const { start, end } = selection;
    if (end > start) {
      const selected = draft.slice(start, end);
      const before = draft.slice(0, start);
      const after = draft.slice(end);
      const next = `${before}**${selected}**${after}`;
      setDraft(next);
      const cursor = start + 2 + selected.length + 2;
      setSelection({ start: cursor, end: cursor });
      focusAtCursor(cursor);
      return;
    }
    insertAtSelection('**bold**');
  };
  const insertHeading = () => {
    const { start } = selection;
    const lineStart = draft.lastIndexOf('\n', Math.max(0, start - 1)) + 1;
    const onEmptyLine = lineStart === start;
    insertAtSelection(onEmptyLine ? '# ' : '\n# ');
  };

  const startEdit = () => {
    setDraft(content ?? '');
    setSelection({ start: (content ?? '').length, end: (content ?? '').length });
    setEditing(true);
  };
  const save = () => {
    onSave(draft);
    setEditing(false);
    // Default back to the short version after an edit.
    setExpanded(false);
  };

  const { preview, truncated } = content
    ? buildPreview(content)
    : { preview: '', truncated: false };
  const showFull = expanded || !truncated;

  return (
    <YStack gap={12}>
      <SectionLabel marginTop={6}>Notes</SectionLabel>

      {editing ? (
        <YStack
          backgroundColor="$surface"
          borderRadius={18}
          overflow="hidden"
          borderWidth={2}
          borderColor="$accentCerulean"
        >
          <TextInput
            ref={inputRef}
            value={draft}
            onChangeText={setDraft}
            onSelectionChange={(e: NativeSyntheticEvent<TextInputSelectionChangeEventData>) =>
              setSelection(e.nativeEvent.selection)
            }
            onBlur={() => {
              if (toolbarInteractingRef.current) {
                toolbarInteractingRef.current = false;
                return;
              }
              save();
            }}
            multiline
            autoFocus
            placeholder={placeholder}
            placeholderTextColor={theme.textSubdued}
            style={{
              minHeight: 140,
              paddingHorizontal: 16,
              paddingTop: 16,
              paddingBottom: 12,
              fontSize: 14,
              color: theme.textOnSurface,
              lineHeight: 20,
              outlineWidth: 0,
              outlineStyle: 'none' as never,
            }}
            textAlignVertical="top"
            accessibilityLabel="Notes editor"
          />
          <XStack
            gap={6}
            paddingHorizontal={12}
            paddingVertical={10}
            borderTopWidth={1}
            borderTopColor="$dividerSubtle"
            backgroundColor="$surfaceCanvas"
          >
            <ToolbarBtn icon="title" label="H1" toolbarInteractingRef={toolbarInteractingRef} onPress={insertHeading} />
            <ToolbarBtn icon="format-bold" label="B" toolbarInteractingRef={toolbarInteractingRef} onPress={insertBold} />
            <ToolbarBtn icon="check-box" label="" toolbarInteractingRef={toolbarInteractingRef} onPress={insertChecklist} />
            <ToolbarBtn icon="tag" label="" toolbarInteractingRef={toolbarInteractingRef} onPress={insertTag} />
          </XStack>
        </YStack>
      ) : !content ? (
        <Pressable
          onPress={canEdit ? startEdit : undefined}
          accessibilityLabel={canEdit ? 'Add notes' : 'Notes'}
          style={{
            backgroundColor: theme.surface,
            borderRadius: 18,
            padding: 16,
            minHeight: 90,
          }}
        >
          <Text color="$textSubdued" fontSize={14}>
            {canEdit
              ? 'No notes yet — tap to add. Use the toolbar for checkboxes, hashtags, and bold.'
              : 'No notes yet.'}
          </Text>
        </Pressable>
      ) : (
        <YStack backgroundColor="$surface" borderRadius={18} padding={16} gap={10}>
          <Pressable
            // Collapsed → first tap expands; expanded → tap opens the editor.
            onPress={!showFull ? () => setExpanded(true) : canEdit ? startEdit : undefined}
            accessibilityLabel={
              !showFull ? 'Expand notes' : canEdit ? 'Open notes editor' : 'Notes'
            }
          >
            <NoteMarkdown
              content={showFull ? content : preview}
              toggleOverrides={overrides}
              onToggle={
                showFull && canEdit
                  ? (idx) => setOverrides((prev) => ({ ...prev, [idx]: !prev[idx] }))
                  : undefined
              }
            />
          </Pressable>
          {truncated ? (
            <Pressable
              onPress={() => setExpanded((v) => !v)}
              accessibilityLabel={showFull ? 'Show less' : 'Show more'}
              hitSlop={12}
              style={({ pressed }) => [
                { flexDirection: 'row', alignItems: 'center', gap: 4, alignSelf: 'flex-start' },
                pressed && { opacity: 0.6 },
              ]}
            >
              <Icon
                name={showFull ? 'expand-less' : 'expand-more'}
                size={16}
                color={theme.accentCerulean}
              />
              <Text fontSize={12} fontWeight="700" color="$accentCerulean">
                {showFull ? 'Show less' : 'Show more'}
              </Text>
            </Pressable>
          ) : null}
        </YStack>
      )}
    </YStack>
  );
}

function ToolbarBtn({
  icon,
  label,
  onPress,
  toolbarInteractingRef,
}: {
  icon: IconName;
  label: string;
  onPress: () => void;
  toolbarInteractingRef: React.MutableRefObject<boolean>;
}) {
  const theme = useThemeColors();
  return (
    <Pressable
      onPress={onPress}
      onPressIn={() => { toolbarInteractingRef.current = true; }}
      accessibilityLabel={label || `Insert ${String(icon)}`}
      style={({ pressed }) => [
        {
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          paddingHorizontal: 10,
          height: 32,
          borderRadius: 999,
          backgroundColor: theme.surfaceCanvas,
        },
        pressed && { backgroundColor: theme.borderMuted },
      ]}
    >
      <Icon name={icon} size={16} color={theme.textOnSurface} />
      {label ? <Text fontSize={12} fontWeight="700" color="$textOnSurface">{label}</Text> : null}
    </Pressable>
  );
}
